import FilterList from "@mui/icons-material/FilterList";
import { Alert, Box, FormControlLabel, LinearProgress, Switch, Tooltip } from "@mui/material";
import {
  DataGridPro,
  FilterPanelTrigger,
  Toolbar,
  ToolbarButton,
  useGridApiContext,
} from "@mui/x-data-grid-pro";
import { useEffect, useState } from "react";
import { useSegmentRelatedMoments } from "../../../../server/use-segment-related-moments";
import { useRelatedMomentsColumns } from "../../hooks/use-related-moments-columns";

import type { UUID } from "../../../../../../utils/uuid";
import type { MomentStatusDetail } from "../../../../../moment/server/supabase-moment-service";
import type { GridFilterModel, GridSortModel } from "@mui/x-data-grid-pro";
import type { FC } from "react";

export const SegmentRelatedMomentsDataGrid: FC<{ segmentId: UUID }> = ({ segmentId }) => {
  const [sortModel, setSortModel] = useState<GridSortModel>([
    { field: "last_status_update", sort: "desc" },
  ]);

  const [filterModel, setFilterModel] = useState<GridFilterModel>({ items: [] });

  const { data, loading, error, reload } = useSegmentRelatedMoments(segmentId);
  const columns = useRelatedMomentsColumns({ moments: data ?? undefined, reload });

  if (loading) return <LinearProgress />;
  if (error) return <Alert severity="error">Unable to load related Communications</Alert>;

  return (
    <DataGridPro<MomentStatusDetail>
      pagination
      pageSizeOptions={[10, 25, 50, 100]}
      initialState={{
        pagination: {
          paginationModel: { pageSize: 50, page: 0 },
          rowCount: 0,
        },
      }}
      disableRowSelectionOnClick
      getRowHeight={() => "auto"}
      rows={data}
      columns={columns}
      slots={{ toolbar: EdxitSegmentToolbar }}
      showToolbar
      sortModel={sortModel}
      onSortModelChange={(model) => setSortModel(model)}
      filterModel={filterModel}
      onFilterModelChange={(model) => setFilterModel(model)}
    />
  );
};

const EdxitSegmentToolbar: FC = () => {
  const [active, setActive] = useState(false);

  const { upsertFilterItem, deleteFilterItem } = useGridApiContext().current;

  useEffect(() => {
    if (active) {
      upsertFilterItem({
        field: "status",
        operator: "equals",
        value: "active",
      });
    } else {
      deleteFilterItem({ field: "status", operator: "equals" });
    }
  }, [active, upsertFilterItem, deleteFilterItem]);

  return (
    <Toolbar>
      <Tooltip title="Filters">
        <FilterPanelTrigger render={<ToolbarButton size="small" />}>
          <FilterList fontSize="inherit" /> &#xA0; Filters
        </FilterPanelTrigger>
      </Tooltip>
      <Box sx={{ flexGrow: 1 }} />
      <FormControlLabel
        control={
          <Switch
            checked={active}
            onChange={() => setActive(!active)}
            data-analytics-id="segment_related_moments_show_active_moments_toggle"
          />
        }
        label="Show active Communications only"
        labelPlacement="start"
      />
    </Toolbar>
  );
};
